import { currentLocale } from '../i18n';
import { api } from './client';
import type { OpeningHoursPeriod } from './places.api';
import type { FlightProposal, HotelProposal, BookingProposal } from './import.api';
import type { InputUnit, MealSlot } from '../types';

export interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
}

// ─── Proposal payloads (server returns these — nothing has been persisted) ─

export interface VenueProposal {
  pendingId: string;
  name:      string;
  category:  string;
  type:      string;
  zone?:     string;
  address?:  string;
  mapsUrl?:  string;
  placeId?:  string;
  latitude?: number | null;
  longitude?:number | null;
  notes?:    string;
  bookingUrl?: string;
  rating?:           number;
  userRatingsTotal?: number;
  priceLevel?:       number;
  photoUrl?:         string;
  /** Every photo the Place Details response returned; `photoUrl` is the first. */
  photoUrls?:        string[];
  openNow?:          boolean;
  openingHoursPeriods?: OpeningHoursPeriod[];
  website?:          string;
  phone?:            string;
}

// A NEW place the AI schedules that isn't saved yet: accepting the activity/day
// plan first creates this in the Places list, then links the activity to it.
export interface NewVenueProposal {
  name:              string;
  category:          string;
  zone?:             string | null;
  address?:          string | null;
  mapsUrl?:          string | null;
  placeId?:          string | null;
  latitude?:         number | null;
  longitude?:        number | null;
  notes?:            string | null;
  bookingUrl?:       string | null;
  rating?:           number | null;
  userRatingsTotal?: number | null;
  priceLevel?:       number | null;
  photoUrl?:         string | null;
  photoUrls?:        string[] | null;
  openNow?:          boolean | null;
  openingHoursPeriods?: OpeningHoursPeriod[] | null;
  website?:          string | null;
  phone?:            string | null;
}

// Saved places the AI proposes removing. `all` marks the "empty the whole list"
// request, so the card can say so instead of just listing 36 names.
export interface VenueDeleteProposal {
  pendingId: string;
  all:       boolean;
  venues:    { id: string; name: string }[];
  /** Stops that go with these places. Counted when proposing, not on accept. */
  removedActivities?: number;
}

/**
 * New notes for a place the trip already has. `before` is what the server read
 * from the database when the proposal was made, never what the model said was
 * there — the card's job is showing the user what is really being replaced.
 */
export interface VenueNotesProposal {
  pendingId: string;
  venueId:   string;
  venueName: string;
  before:    string;
  after:     string;
}

export interface ActivityProposal {
  pendingId:     string;
  dayId:         string;
  title:         string;
  type:          string;
  startTime?:    string | null;
  durationMins?: number | null;
  venueName?:    string | null;
  venueId?:      string | null;
  zone?:         string | null;
  address?:      string | null;
  notes?:        string | null;
  bookingUrl?:   string | null;
  latitude?:     number | null;
  longitude?:    number | null;
  placeId?:      string | null;
  newVenue?:     NewVenueProposal | null;
  /** The stop this one goes right after. Absent/null = at the end of the day. */
  afterActivityId?:    string | null;
  /** Its title, so the card can say it before the user accepts. */
  afterActivityTitle?: string | null;
}

export interface ActivityMoveProposal {
  pendingId:  string;
  activityId: string;
  title:      string;
  fromDayId:  string;
  toDayId:    string;
}

export interface DayPlanActivityItem {
  title:         string;
  type:          string;
  startTime?:    string | null;
  durationMins?: number | null;
  venueName?:    string | null;
  venueId?:      string | null;
  zone?:         string | null;
  address?:      string | null;
  notes?:        string | null;
  bookingUrl?:   string | null;
  latitude?:     number | null;
  longitude?:    number | null;
  placeId?:      string | null;
  newVenue?:     NewVenueProposal | null;
}

export interface DayPlanProposal {
  pendingId:   string;
  dayId:       string;
  dayNumber?:  number;
  date?:       string;
  activities:  DayPlanActivityItem[];
}

export interface ReorderProposal {
  pendingId:   string;
  dayId:       string;
  dayNumber?:  number;
  activityIds: string[];
  activities:  { id: string; title: string }[];
}

export interface DeleteProposal {
  pendingId:  string;
  activityId: string;
  title:      string;
  dayId:      string;
}

export interface PackingProposal {
  pendingId: string;
  sections:  Array<{ title: string; items: string[] }>;
}

export interface TipsProposal {
  pendingId: string;
  tips:      Array<{ category: string; content: string }>;
}

export interface ExpenseProposal {
  pendingId:   string;
  description: string;
  amount:      number;
  category:    string;
  notes?:      string | null;
}

// Expenses the AI proposes removing. `all` marks the "empty the whole ledger"
// request, so the card can say so instead of just listing every description.
export interface ExpenseDeleteProposal {
  pendingId: string;
  all:       boolean;
  expenses:  { id: string; description: string; amount: number }[];
}

// Budget caps the AI proposes for the trip. Restricted to the dimensions it can
// fill on its own — PERSON is excluded (needs participant IDs). On accept the
// client merges these into the trip's existing budget limits.
export interface BudgetProposal {
  pendingId: string;
  limits: Array<{
    scope:     'TOTAL' | 'DAILY' | 'CATEGORY';
    category?: string | null;
    amount:    number;
  }>;
}

export interface RouteOptimizeProposal {
  pendingId:  string;
  orderedIds: string[];
  legModes?:  string[];
  reasoning:  string;
  benefits:   string[];
}

// ─── ADR-0027 phase 2: edits and the rest of the trip ─────────────────────

type ActivityFields = { title?: string; startTime?: string | null; durationMins?: number | null; notes?: string | null };

/** A stop already in the itinerary; the card shows `before` → `changes`. */
export interface ActivityUpdateProposal {
  pendingId:  string;
  activityId: string;
  dayId:      string;
  title:      string;
  changes:    ActivityFields;
  before:     ActivityFields;
}

/** Items for sections the viewer may write, and existing items to (un)check. */
export interface PackingItemsProposal {
  pendingId: string;
  add:   { sectionId: string; sectionTitle: string; text: string }[];
  check: { sectionId: string; itemId: string; text: string; checked: boolean }[];
}

export interface NoteProposal {
  pendingId: string;
  title:     string;
  content:   string;
}

export interface MealProposal {
  pendingId:   string;
  dayId:       string;
  dayNumber:   number;
  slot:        MealSlot;
  title:       string;
  notes:       string | null;
  venueId:     string | null;
  venueName:   string | null;
  ingredients: { name: string; amount: number; unit: InputUnit }[];
}

export interface GroceryProposal {
  pendingId: string;
  items:     { text: string; amount: number; unit: InputUnit }[];
}

/** ADR-0028: what the assistant may edit or delete, applied by POST /ai/apply. */
export type AiEntity =
  | 'day' | 'venue' | 'flight' | 'hotel' | 'booking' | 'expense' | 'note' | 'tip'
  | 'packing_section' | 'packing_item' | 'outfit_piece' | 'meal' | 'grocery_item';
export type AiDataArea = 'itinerary' | 'places' | 'bookings' | 'expenses' | 'notes' | 'tips' | 'packing' | 'meals';

export interface EntityUpdateProposal {
  pendingId: string;
  entity:    AiEntity;
  area:      AiDataArea;
  id:        string;
  label:     string;
  changes:   Record<string, unknown>;
  before:    Record<string, unknown>;
}

export interface EntityDeleteProposal {
  pendingId: string;
  entity:    AiEntity;
  area:      AiDataArea;
  items:     { id: string; label: string }[];
}

export type AiOperation = 'trip_dates' | 'settle_debt' | 'outfit_add' | 'ingredient_add' | 'meal_stop' | 'activities_duplicate';

/** A line of an operation card: an i18n key and its params, written by the client in the reader's language. */
export interface AiLine { key: string; params?: Record<string, string | number> }

export interface OperationProposal {
  pendingId: string;
  op:        AiOperation;
  area:      AiDataArea;
  input:     unknown;
  title:     AiLine;
  lines:     AiLine[];
}

export type AiApplyRequest =
  | { kind: 'update'; entity: AiEntity; id: string; changes: Record<string, unknown> }
  | { kind: 'delete'; entity: AiEntity; ids: string[] }
  | { kind: 'operation'; op: AiOperation; input: unknown };

export type AiAction =
  | { type: 'venue_proposed';          data: VenueProposal;          dayId?: string }
  | { type: 'venue_delete_proposed';   data: VenueDeleteProposal;    dayId?: string }
  | { type: 'venue_notes_proposed';    data: VenueNotesProposal;     dayId?: string }
  | { type: 'activity_proposed';       data: ActivityProposal;       dayId?: string }
  | { type: 'activity_move_proposed';  data: ActivityMoveProposal;   dayId?: string }
  | { type: 'day_plan_proposed';       data: DayPlanProposal;        dayId?: string }
  | { type: 'reorder_proposed';        data: ReorderProposal;        dayId?: string }
  | { type: 'delete_proposed';         data: DeleteProposal;         dayId?: string }
  | { type: 'packing_proposed';        data: PackingProposal;        dayId?: string }
  | { type: 'tips_proposed';           data: TipsProposal;           dayId?: string }
  | { type: 'expense_proposed';        data: ExpenseProposal;        dayId?: string }
  | { type: 'expense_delete_proposed'; data: ExpenseDeleteProposal;  dayId?: string }
  | { type: 'budget_proposed';         data: BudgetProposal;         dayId?: string }
  | { type: 'route_optimize_proposed'; data: RouteOptimizeProposal;  dayId?: string }
  | { type: 'activity_update_proposed'; data: ActivityUpdateProposal; dayId?: string }
  | { type: 'packing_items_proposed';  data: PackingItemsProposal;   dayId?: string }
  | { type: 'note_proposed';           data: NoteProposal;           dayId?: string }
  | { type: 'flight_proposed';         data: FlightProposal;         dayId?: string }
  | { type: 'hotel_proposed';          data: HotelProposal;          dayId?: string }
  | { type: 'booking_proposed';        data: BookingProposal;        dayId?: string }
  | { type: 'meal_proposed';           data: MealProposal;           dayId?: string }
  | { type: 'grocery_proposed';        data: GroceryProposal;        dayId?: string }
  | { type: 'entity_update_proposed';  data: EntityUpdateProposal;   dayId?: string }
  | { type: 'entity_delete_proposed';  data: EntityDeleteProposal;   dayId?: string }
  | { type: 'operation_proposed';      data: OperationProposal;      dayId?: string };

// Domain the orchestrator routed the message to (when context was 'auto').
// Lets the panel show which specialist actually answered.
export type ResolvedContext =
  | 'places' | 'itinerary' | 'packing' | 'tips' | 'bookings' | 'expenses' | 'allroutes' | 'routes' | 'general';

export interface AiChatResponse {
  reply: string;
  actions: AiAction[];
  model: 'gpt-4o' | 'gpt-4o-mini' | 'gpt-5.6-luna';
  resolvedContext: ResolvedContext;
  aiCreditsLeft: number;
  premiumAiCreditsLeft: number;
  // Set when a tool tried to enrich a venue via Google Places and the
  // user's monthly Maps quota was already exceeded — the AI still replies
  // normally (degraded, unenriched data), this just lets the UI warn about it.
  mapsQuotaExceeded: { resetsAt?: string } | null;
}

// 'auto' lets the backend orchestrator pick the specialist; a concrete context
// pins it (map route flows). `hintContext` biases 'auto' routing with the tab
// the user is chatting from.
export type ChatContext =
  | 'auto' | 'places' | 'itinerary' | 'routes' | 'packing' | 'tips' | 'allroutes' | 'bookings' | 'expenses';
export type HintContext = 'places' | 'itinerary' | 'packing' | 'tips' | 'bookings' | 'expenses';

/**
 * The proposal cards this build can draw (server: mastra/cards.ts). The server
 * only offers the model the tools whose cards the caller declared; bump it when
 * this client learns a new card type.
 */
export const AI_CARDS_VERSION = 2;

export const aiApi = {
  chat: (
    tripId: string,
    messages: AiMessage[],
    context: ChatContext,
    extra?: { dayId?: string; pendingNames?: string[]; autoAccept?: boolean; hintContext?: HintContext },
  ) =>
    // `locale` is read here rather than taken from the caller so no call site can
    // forget it: without it the server can only guess the reply language from the
    // message text, which is what made one trip answer in several languages.
    // It drives the conversational reply only — what gets saved into the trip
    // follows the trip's own contentLanguage, server-side.
    api.post<AiChatResponse>(`/trips/${tripId}/ai/chat`, {
      messages, context, locale: currentLocale(), aiCards: AI_CARDS_VERSION, ...extra,
    }).then(r => r.data),
  /** ADR-0028: the server re-checks the accepted card and writes it with the tab's own services. */
  apply: (tripId: string, body: AiApplyRequest) =>
    api.post<unknown>(`/trips/${tripId}/ai/apply`, body).then(r => r.data),
};
